import React, { useState, useEffect } from 'react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import { ArrowRight } from 'lucide-react';
import Link from 'next/link';

const BlogPage = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const res = await fetch('/projectbim/api/blogs');
                if (res.ok) {
                    const data = await res.json();
                    setBlogs(data);
                } else {
                    setError('Failed to load blogs. Please try again later.');
                }
            } catch (err) {
                setError('Could not connect to the database.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchBlogs();
    }, []);

    const getPreviewText = (content) => {
        if (!content) return "";
        try {
            if (content.startsWith('[') || content.startsWith('{')) {
                const blocks = JSON.parse(content);
                return blocks
                    .filter(b => b.type === 'text')
                    .map(b => b.value)
                    .join(' ')
                    .substring(0, 100) + "...";
            }
        } catch (e) { }
        return content.length > 55 ? content.substring(0, 55) + "......." : content;
    };

    const formatDate = (dateString) => {
        if (!dateString) return "Just now";
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const cleanPath = (path) => {
        if (!path) return null;
        if (path.startsWith('data:') || path.startsWith('http')) return path;
        
        // Ensure path starts with a slash
        let finalPath = path.startsWith('/') ? path : '/' + path;
        
        // CASE 1: Path already includes projectbim/uploads (database legacy)
        if (finalPath.startsWith('/projectbim/uploads/')) {
            return finalPath.replace('/projectbim/uploads/', '/projectbim/api/uploads/');
        }

        // CASE 2: Path is just /uploads/
        if (finalPath.startsWith('/uploads/')) {
            return '/projectbim/api' + finalPath;
        }
        
        // CASE 3: Path is already correct
        if (finalPath.startsWith('/projectbim/api/uploads/')) {
            return finalPath;
        }

        if (!finalPath.startsWith('/projectbim/')) {
            finalPath = '/projectbim' + finalPath;
        }
        
        return finalPath;
    };

    return (
        <div style={{ backgroundColor: 'var(--color-black)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

            <div style={{ padding: '80px 20px', maxWidth: '1200px', margin: '0 auto', flex: 1, width: '100%', boxSizing: 'border-box' }}>
                <h1 style={{ color: 'var(--color-white)', fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 'bold', marginBottom: '15px', textAlign: 'center', fontFamily: 'var(--font-heading)' }}>
                    Our Latest <span style={{ color: 'var(--color-primary)' }}>Insights</span>
                </h1>
                <p style={{ color: '#ffffffff', textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px', fontSize: '18px' }}>
                    Explore our expert articles on BIM, Architecture, and the future of AEC technology.
                </p>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#54b248' }}>
                        <h3>Loading Blogs...</h3>
                    </div>
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: '50px', color: '#ff4d4d' }}>
                        <p>{error}</p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                        gap: '30px'
                    }}>
                        {Array.isArray(blogs) && blogs.length > 0 ? (
                            blogs
                                .filter(blog => blog.status !== 'draft')
                                .map((blog) => (
                                    <Link href={`/blog/${blog.slug}`} key={blog.id} style={{ textDecoration: 'none', display: 'flex' }}>
                                        <div className="blog-card" style={{
                                            flex: 1,
                                            height: '100%',
                                            cursor: 'pointer',
                                            backgroundColor: '#fff',
                                            borderRadius: '12px',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s ease',
                                            border: '1px solid #eee'
                                        }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-10px)';
                                                e.currentTarget.style.boxShadow = '0 20px 40px rgba(0,0,0,0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}>
                                            <div style={{ height: '220px', width: '100%', overflow: 'hidden' }}>
                                                <img
                                                    src={cleanPath(blog.image_url) || 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?q=80&w=2831&auto=format&fit=crop'}
                                                    alt={blog.title}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>

                                            <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                <span style={{ color: '#ffffffff', fontSize: '13px', display: 'block' }}>
                                                    {formatDate(blog.created_at)}
                                                </span>

                                                <h3 style={{ color: 'var(--color-primary)', fontSize: '18px', fontWeight: 'bold', margin: 0, lineHeight: '1.3', display: '-webkit-box', WebkitLineClamp: '1', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {blog.title}
                                                </h3>

                                                <p style={{ color: '#000', fontSize: '16px', fontWeight: 'bold', lineHeight: '1.4', margin: 0, display: '-webkit-box', WebkitLineClamp: '1', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                    {blog.description || getPreviewText(blog.content)}
                                                </p>

                                                <div style={{ marginTop: 'auto', paddingTop: '10px', display: 'flex', alignItems: 'center', color: 'var(--color-primary)', fontSize: '14px', fontWeight: 'bold', gap: '5px' }}>
                                                    Learn More <ArrowRight size={16} />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))
                        ) : (
                            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '100px 20px', color: '#666' }}>
                                <h3 style={{ fontSize: '28px', color: '#ffffffff' }}>No Blogs Published Yet</h3>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogPage;
